import React, { useEffect, useState } from 'react';
import { getAllConfigs, type ConfigDTO } from '../services/ConfigService';
import { useNotification } from '../components/Notifications';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../security/AuthContext';
import { hasRole } from '../services/AuthService';

export function Config() {
  const [configs, setConfigs] = useState<ConfigDTO[]>([]);
  const [settings, setSettings] = useState<ConfigDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const {addNotification} = useNotification();
  const navigate = useNavigate();
  const {user} = useAuth();

  useEffect(() => {
    if (!hasRole(user, 'admin')) {
      navigate('/unauthorized');
      return;
    }

    getAllConfigs()
    .then((data) => {
      setConfigs(data);
      setSettings(data);
    }).catch((err) => {
      addNotification('Failed to load conifgs ' + err.message, 'error');
    });
  }, []);

  
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = configs.filter(setting =>
      setting.name.toLowerCase().includes(term)
    );
    setSettings(filtered);
  };

  const startEditing = (id, value) => {
    setEditingId(id);
    setEditValue(value);
  };

  const saveEdit = (id) => {
    setSettings(settings.map(setting =>
      setting.id === id ? { ...setting, value: editValue } : setting
    ));
    setEditingId(null);
  };

  const handleKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      saveEdit(id);
    }
    if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  return (
    <div className="max-w-7xl relative">

      <div className="bg-white shadow-md rounded-t-lg overflow-visible">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-700">System settings</h2>
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Setting..."
              className="pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400"
            />
            <i className="fa-solid fa-magnifying-glass text-indigo-500 absolute right-3 top-1/2 transform -translate-y-1/2 text-lg"></i>
          </div>
        </div>
      </div>


      <div className="relative overflow-visible">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 px-6 border-b text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="py-3 px-6 border-b text-left text-xs font-medium text-gray-500 uppercase">Value</th>
              <th className="py-3 px-8 border-b text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 relative z-0">
            {settings.length > 0 ? (
              settings.map((setting, idx) => {
                const isLast = idx === settings.length - 1;
                return (
                <tr key={setting.id} className="cursor-pointer">
                  <td colSpan={3} className="p-0">
                    <div
                      className={`grid grid-cols-[2fr_2fr_3fr] items-center px-6 py-4 ${isLast ? "rounded-b-lg shadow-sm" : ""} bg-white hover:bg-indigo-50`}
                      style={{
                        borderBottomLeftRadius: isLast ? "0.5rem" : undefined,
                        borderBottomRightRadius: isLast ? "0.5rem" : undefined,
                      }}
                    >
                      <div className="text-sm text-gray-900 whitespace-nowrap">
                        {setting.name}
                      </div>
                      <div className="text-sm text-gray-500 whitespace-nowrap">
                        {editingId === setting.id ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => saveEdit(setting.id)}
                            onKeyDown={(e) => handleKeyPress(e, setting.id)}
                            className="w-auto p-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => startEditing(setting.id, setting.value)}
                            className="cursor-pointer hover:text-blue-700"
                          >
                            {setting.value}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 whitespace-nowrap">
                        {setting.comment}
                      </div>  
                    </div>  
                  </td>  
                </tr>
              )})
            ) : (
              <tr>
                <td colSpan={3} className="py-2 px-4 text-center text-gray-500">
                  No settings found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

